
import { Save, Loader2, Mic, Volume2 } from 'lucide-react';
import { cn } from '../ui/utils';

interface ActionButtonsProps {
    onSave: () => void;
    onCancel: () => void;
    isSaving: boolean;
    hasUnsavedChanges: boolean;
    isDarkMode: boolean;
    onVoiceRecord?: () => void;
    isVoiceListening?: boolean;
    voiceTranscript?: string;
    voiceInterimTranscript?: string;
}

export function ActionButtons({
    onSave,
    onCancel,
    isSaving,
    hasUnsavedChanges,
    isDarkMode,
    onVoiceRecord,
    isVoiceListening,
    voiceTranscript,
    voiceInterimTranscript,
    isSubmitted = false
}: ActionButtonsProps & { isSubmitted?: boolean }) {

    // Show live transcript while listening
    const showTranscript = isVoiceListening && (voiceTranscript || voiceInterimTranscript);
    
    const getButtonConfig = () => {
        if (isSaving) return {
            text: 'Saving...',
            className: isDarkMode ? 'bg-indigo-600' : 'bg-indigo-600 text-white',
            disabled: true
        };

        if (isSubmitted && !hasUnsavedChanges) return {
            text: 'Saved',
            className: 'bg-emerald-600 text-white hover:bg-emerald-700',
            disabled: true
        };

        if (isSubmitted && hasUnsavedChanges) return {
            text: 'Save Again?',
            className: 'bg-amber-500 text-white hover:bg-amber-600 shadow-amber-500/25',
            disabled: false
        };

        if (!hasUnsavedChanges) return {
            text: 'Saved', // Draft saved
            className: isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-400',
            disabled: true
        };

        return {
            text: 'Submit Entry',
            className: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-500/25 hover:shadow-blue-500/40',
            disabled: false
        };
    };

    const config = getButtonConfig();

    return (
        <div className={cn(
            "fixed bottom-0 left-0 right-0 p-4 border-t z-50 flex flex-col gap-2 transition-colors",
            isDarkMode
                ? "bg-slate-950 border-slate-800"
                : "bg-white border-gray-100"
        )}>
            {/* Live Voice Transcript Display */}
            {showTranscript && (
                <div className={cn(
                    "max-w-3xl mx-auto w-full px-4 py-2 rounded-xl flex items-center gap-2 animate-in fade-in duration-200",
                    isDarkMode
                        ? "bg-blue-950/50 border border-blue-800/50"
                        : "bg-blue-50 border border-blue-200"
                )}>
                    <Volume2 className={cn(
                        "w-4 h-4 flex-shrink-0 animate-pulse",
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                    )} />
                    <span className={cn(
                        "text-sm truncate",
                        isDarkMode ? "text-blue-300" : "text-blue-700"
                    )}>
                        {voiceInterimTranscript || voiceTranscript || 'Listening...'}
                    </span>
                </div>
            )}
            
            <div className="flex-1 flex gap-3 max-w-3xl mx-auto w-full">
                <button
                    onClick={onCancel}
                    className={cn(
                        "px-6 py-3.5 rounded-2xl font-semibold text-sm transition-all active:scale-95",
                        isDarkMode
                            ? "bg-slate-900 text-slate-300 hover:bg-slate-800"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                >
                    Cancel
                </button>

                {onVoiceRecord && (
                    <button
                        onClick={onVoiceRecord}
                        className={cn(
                            "p-3.5 rounded-2xl transition-all active:scale-95 flex items-center justify-center aspect-square",
                            isVoiceListening
                                ? "bg-red-500 text-white animate-pulse"
                                : isDarkMode
                                    ? "bg-slate-800 text-blue-400 hover:bg-slate-700"
                                    : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                        )}
                    >
                        <Mic className="w-5 h-5" />
                    </button>
                )}

                <button
                    onClick={onSave}
                    disabled={config.disabled}
                    className={cn(
                        "flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-bold text-sm shadow-lg transition-all active:scale-95",
                        config.className
                    )}
                >
                    {isSaving ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {config.text}
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4" />
                            {config.text}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
